/**
 * @fileoverview Chat state management hook for handling messages and chat UI state.
 * Provides centralized state management for the chat interface including messages,
 * streaming status, and SSE connection management.
 *
 * @module hooks/chat/useChatState
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { useAuth } from '../auth/useAuth'

/**
 * Custom hook for managing chat state and message operations.
 * Provides methods to add, update, and manage chat messages along with
 * streaming state and SSE connection lifecycle.
 *
 * @returns {Object} Chat state and methods
 * @returns {Array} returns.messages - Array of chat messages
 * @returns {boolean} returns.isChatMode - Whether chat mode is active
 * @returns {boolean} returns.isStreaming - Whether SSE streaming is active
 * @returns {number} returns.updateCounter - Counter to force UI re-renders
 * @returns {string} returns.assistantAnimationState - Current animation state for assistant messages
 * @returns {Object} returns.sseRef - Reference to current SSE connection
 * @returns {Function} returns.addUserMessage - Adds a user message
 * @returns {Function} returns.addAssistantPlaceholder - Adds assistant placeholder
 * @returns {Function} returns.updateAssistantMessage - Updates assistant message (legacy API)
 * @returns {Function} returns.addMessagePart - Adds a part to assistant message (new parts API)
 * @returns {Function} returns.createMessageWithParts - Creates message with parts structure
 * @returns {Function} returns.removeLastUserMessage - Removes last user message
 * @returns {Function} returns.setIsStreaming - Sets streaming state
 * @returns {Function} returns.cleanupSSE - Cleans up SSE connection
 * @returns {Function} returns.getConversationHistory - Gets conversation history
 * @returns {Function} returns.getMessageCount - Gets user message count
 * @returns {Function} returns.clearMessages - Clears all messages
 *
 * @example
 * const {
 *   messages,
 *   isChatMode,
 *   addUserMessage,
 *   updateAssistantMessage
 * } = useChatState()
 */
export const useChatState = () => {
  const {
    conversationHistory,
    updateConversationHistory,
    getOrCreateConversationId,
    clearConversationId,
    generateMessageId,
  } = useAuth()
  const [messages, setMessages] = useState([])
  const [isChatMode, setIsChatMode] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [updateCounter, setUpdateCounter] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)
  const [assistantAnimationState, setAssistantAnimationState] = useState('idle')
  const sseRef = useRef(null)
  const previousMessagesRef = useRef([])

  /**
   * Helper function to determine animation state based on message type and streaming status.
   * Used to manage chat bubble animations during different phases of AI processing.
   *
   * @param {string} messageType - Type of message ('status', 'progress', 'response', etc.)
   * @param {boolean} currentIsStreaming - Whether SSE streaming is currently active
   * @returns {string} Animation state ('idle', 'thinking', 'processing', 'streaming')
   */
  const getAnimationStateForMessage = useCallback((messageType, currentIsStreaming) => {
    if (messageType === 'status') return 'thinking'
    if (messageType === 'progress') return 'processing'
    if (messageType === 'response' && currentIsStreaming) return 'streaming'
    return 'idle'
  }, [])

  /**
   * Triggers completion animation when streaming ends.
   * Shows brief flash animation then returns to idle state.
   */
  const triggerCompletionAnimation = useCallback(() => {
    setAssistantAnimationState('complete')
    // Return to idle after completion animation duration (600ms)
    setTimeout(() => {
      setAssistantAnimationState('idle')
    }, 600)
  }, [])

  /**
   * Load conversation history from AuthContext on component mount.
   * Initializes chat mode if messages exist.
   */
  useEffect(() => {
    // Only load conversation history once on mount
    if (!isInitialized) {
      if (conversationHistory && conversationHistory.length > 0) {
        setMessages(conversationHistory)
        setIsChatMode(true)
      }
      setIsInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally empty to load only once on mount

  // Watch for conversation history changes after sign-in
  useEffect(() => {
    // If we're already initialized but conversation history changed and we have no messages,
    // this might be a sign-in event that should restore messages
    if (
      isInitialized &&
      conversationHistory &&
      conversationHistory.length > 0 &&
      messages.length === 0
    ) {
      setMessages(conversationHistory)
      setIsChatMode(true)
    }
  }, [conversationHistory, isInitialized, messages.length])

  // Listen for conversation-resumed events from login/OAuth flows
  useEffect(() => {
    const handleConversationResumed = (event) => {
      const resumeData = event.detail

      if (resumeData && resumeData.operation === 'resume_conversation') {
        // Update chat state with the resumed conversation
        if (resumeData.data.messages && resumeData.data.messages.length > 0) {
          setMessages(resumeData.data.messages)
          setIsChatMode(true)
        }
      }
    }

    // Listen for custom conversation resumed events
    window.addEventListener('conversation-resumed', handleConversationResumed)

    return () => {
      window.removeEventListener('conversation-resumed', handleConversationResumed)
    }
  }, [])

  // Watch for streaming state changes to trigger completion animation
  useEffect(() => {
    // When streaming stops and we have messages, trigger completion animation
    if (!isStreaming && messages.length > 0) {
      const lastMessage = messages[messages.length - 1]

      // Only trigger completion for assistant messages that have actual content
      if (
        lastMessage &&
        lastMessage.role === 'assistant' &&
        !lastMessage.isProgress &&
        (lastMessage.parts?.length > 0 || lastMessage.content)
      ) {
        // Only trigger if we were previously streaming (not just idle)
        if (assistantAnimationState === 'streaming') {
          triggerCompletionAnimation()
        }
      }
    }
  }, [isStreaming, messages, assistantAnimationState, triggerCompletionAnimation])

  /**
   * Save conversation history via AuthContext whenever messages change.
   * Only saves after initialization is complete to avoid overwriting
   * loaded data with empty array.
   */
  useEffect(() => {
    if (!isInitialized) {
      return
    }

    // Filter out progress messages and incomplete responses for storage
    const messagesToSave = messages.filter(
      (msg) => msg.role === 'user' || (msg.role === 'assistant' && !msg.isProgress)
    )

    // Only update if the messages have actually changed
    const hasChanged =
      JSON.stringify(messagesToSave) !== JSON.stringify(previousMessagesRef.current)

    if (hasChanged) {
      previousMessagesRef.current = messagesToSave
      updateConversationHistory(messagesToSave)
    }
  }, [messages, isInitialized, updateConversationHistory])

  /**
   * Adds a new user message to the chat.
   * Automatically activates chat mode on first message.
   * Generates UUIDs for conversation and message tracking.
   *
   * @param {string} content - The message content
   */
  const addUserMessage = useCallback(
    (content) => {
      // Generate UUIDs for the user message
      const conversationId = getOrCreateConversationId()
      const messageId = generateMessageId('user')

      const userMessage = {
        role: 'user',
        content,
        messageId,
        conversationId,
        timestamp: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, userMessage])
      if (!isChatMode) {
        setIsChatMode(true)
      }

      // Return the message so caller can access UUIDs immediately
      return userMessage
    },
    [isChatMode, getOrCreateConversationId, generateMessageId]
  )

  /**
   * Creates a new message with parts structure.
   * Used for creating messages that support the new SSE parts storage system.
   *
   * @param {string} role - Message role ('user' or 'assistant')
   * @param {string} initialContent - Initial content for the message
   * @returns {Object} Message object with parts structure
   */
  const createMessageWithParts = useCallback(
    (role, initialContent = '') => {
      const conversationId = getOrCreateConversationId()
      const messageId = generateMessageId(role)

      return {
        // PRIMARY STORAGE KEYS - Use underscore naming
        message_id: messageId,
        conversation_id: conversationId,
        role,
        parts: [],
        timestamp: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),

        // Analytics metadata (only essential fields)
        streamingDuration: 0,

        // State flags (not persisted in storage)
        isProgress: false,
        isError: false,
        currentStatus: null,
        statusType: null,

        // Legacy compatibility fields
        messageId,
        conversationId,
        content: initialContent,
      }
    },
    [getOrCreateConversationId, generateMessageId]
  )

  /**
   * Helper function to compute streaming duration from parts array.
   * Calculates the time between first and last response parts.
   *
   * @param {Array} parts - Array of message parts
   * @returns {number} Duration in milliseconds
   */
  const computeStreamingDuration = useCallback((parts) => {
    if (parts.length < 2) return 0
    const firstTimestamp = new Date(parts[0].timestamp)
    const lastTimestamp = new Date(parts[parts.length - 1].timestamp)
    return lastTimestamp - firstTimestamp
  }, [])

  /**
   * Helper function to construct display content from parts array.
   * This is the only place where content should be constructed - not stored permanently.
   * Only concatenates TEXT content - skips component parts which should be rendered separately.
   *
   * @param {Array} parts - Array of message parts
   * @returns {string} Concatenated content for display (text only)
   */
  const constructContentFromParts = useCallback((parts) => {
    if (!parts || parts.length === 0) return ''

    return parts
      .filter((part) => {
        // Skip component parts - they should be rendered separately by ChatMessageWithParts
        if (part.isComponent) return false

        // Include text-only parts
        return part.type === 'response' || part.type === 'error'
      })
      .map((part) => {
        // If the part has clickable sentences, extract text from sentences array
        if (part.sentences && Array.isArray(part.sentences) && part.sentences.length > 0) {
          return part.sentences.map((sentence) => sentence.text).join(' ')
        }
        // Otherwise use the message field
        return part.message || ''
      })
      .join('')
  }, [])

  /**
   * Helper function to parse component markers and extract component information.
   * Handles markers like [[heading:Title:1]], [[button:Text:action]], etc.
   *
   * @param {string} message - Message content to parse
   * @returns {Object} Component information or null if not a component
   */
  const parseComponentMarker = useCallback((message) => {
    if (!message || typeof message !== 'string') return null

    // Match component markers in the format [[type:param1:param2:...]]
    const componentMatch = message.match(/^\[\[([^:]+):(.+)\]\]$/)
    if (!componentMatch) return null

    const [, componentType, paramsString] = componentMatch
    const params = paramsString.split(':')
    const result = {
      isComponent: true,
      componentType: componentType.toLowerCase(),
      originalMarker: message,
    }

    // Parse component-specific parameters
    switch (componentType.toLowerCase()) {
      case 'heading':
        result.title = params[0] || 'Heading'
        result.level = parseInt(params[1]) || 3
        // Extract title for display
        result.displayText = result.title
        break

      case 'button':
        result.text = params[0] || 'Button'
        result.action = params[1] || ''
        result.displayText = result.text
        break

      case 'link':
        result.text = params[0] || 'Link'
        result.url = params[1] || '#'
        result.displayText = result.text
        break

      case 'badge':
        result.text = params[0] || 'Badge'
        result.variant = params[1] || 'default'
        result.displayText = result.text
        break

      case 'code':
        result.code = params[0] || ''
        result.language = params[1] || 'javascript'
        result.displayText = result.code
        break

      default:
        // For unknown component types, preserve all parameters
        result.params = params
        result.displayText = params[0] || message
        break
    }

    return result
  }, [])

  /**
   * Adds a new part to the last assistant message.
   * Handles status/progress messages (display only, NOT stored) and
   * response/error messages (stored as parts with field mapping).
   * Enhanced to properly parse component markers and set component fields.
   *
   * @param {Object} partData - Part data from SSE stream
   * @param {string} partData.type - Part type ('status', 'progress', 'response', 'error')
   * @param {string} partData.message - Part message content
   * @param {string} [partData.sentenceUUID] - SSE sentence UUID (mapped to sentence_id)
   * @param {string} [partData.messageId] - SSE message ID (mapped to message_id)
   * @param {string} [partData.conversationId] - SSE conversation ID (mapped to conversation_id)
   * @param {number} [partData.order] - Explicit order from SSE (if provided)
   * @param {boolean} [partData.isComponent] - Whether this is a component (from SSE)
   * @param {string} [partData.componentType] - Component type (from SSE)
   */
  const addMessagePart = useCallback(
    (partData) => {
      setMessages((prevMessages) => {
        const newMessages = [...prevMessages]
        const lastIndex = newMessages.length - 1
        const lastMessage = newMessages[lastIndex]

        if (!lastMessage || lastMessage.role !== 'assistant') {
          return prevMessages
        }

        const updatedMessage = { ...lastMessage }

        // Handle status/progress messages (DISPLAY ONLY - NOT STORED)
        if (partData.type === 'status' || partData.type === 'progress') {
          // Update display state for real-time UI feedback
          updatedMessage.isProgress = true
          updatedMessage.currentStatus = partData.message
          updatedMessage.statusType = partData.type
          updatedMessage.lastUpdated = new Date().toISOString()

          // Construct display content from stored parts + current status (not stored)
          const storedContent = constructContentFromParts(lastMessage.parts)
          updatedMessage.content = storedContent + partData.message
        }
        // Handle response/error messages (STORED AS PARTS)
        else if (partData.type === 'response' || partData.type === 'error') {
          // Parse component markers - even if already marked as component we need to extract the data
          let componentInfo = null
          let isComponent = partData.isComponent || false
          let componentType = partData.componentType || null

          // If it's marked as a component OR contains component marker syntax, parse it
          if (partData.message && (isComponent || partData.message.includes('[['))) {
            componentInfo = parseComponentMarker(partData.message)
            if (componentInfo) {
              isComponent = true
              componentType = componentInfo.componentType
              // Component marker parsed successfully
            }
          }

          // Create new part for storage with field mapping
          const newPart = {
            type: partData.type,
            message: partData.message || partData.content || '',
            timestamp: new Date().toISOString(),
            order: partData.order !== undefined ? partData.order : lastMessage.parts.length, // Use SSE order or auto-increment
            isComponent,
            componentType,
            metadata: {
              event_id: `sse-event-${Date.now()}`,
              source: 'chat-api',
              ...partData.metadata,
            },
          }

          // If we parsed component information, add it to the part
          if (componentInfo) {
            Object.assign(newPart, {
              componentData: componentInfo,
              // Store parsed component parameters for easy access
              title: componentInfo.title,
              level: componentInfo.level,
              text: componentInfo.text,
              action: componentInfo.action,
              url: componentInfo.url,
              variant: componentInfo.variant,
              code: componentInfo.code,
              language: componentInfo.language,
              params: componentInfo.params,
              displayText: componentInfo.displayText,
              originalMarker: componentInfo.originalMarker,
            })
          }

          // Map SSE field names to storage field names
          if (partData.sentenceUUID) {
            newPart.sentence_id = partData.sentenceUUID // Map sentenceUUID → sentence_id
          }
          if (partData.messageId || partData.messageUUID) {
            newPart.message_id = partData.messageId || partData.messageUUID // Map to message_id
          }
          if (partData.conversationId || partData.conversationUUID) {
            newPart.conversation_id = partData.conversationId || partData.conversationUUID // Map to conversation_id
          }

          // Spread remaining fields (excluding the ones we mapped above)
          const {
            sentenceUUID: _sentenceUUID, // eslint-disable-line no-unused-vars
            messageUUID: _messageUUID, // eslint-disable-line no-unused-vars
            conversationUUID: _conversationUUID, // eslint-disable-line no-unused-vars
            type: _type, // eslint-disable-line no-unused-vars
            message: _message, // eslint-disable-line no-unused-vars
            metadata: _metadata, // eslint-disable-line no-unused-vars
            order: _order, // eslint-disable-line no-unused-vars
            isComponent: _isComponent, // eslint-disable-line no-unused-vars
            componentType: _componentType, // eslint-disable-line no-unused-vars
            ...remainingFields
          } = partData

          // Preserve ALL remaining SSE fields in the part
          // This includes components, sentences, isTLDR, etc.
          Object.assign(newPart, remainingFields)

          // Add to parts array (only response/error types get stored)
          updatedMessage.parts = [...lastMessage.parts, newPart]
          updatedMessage.lastUpdated = newPart.timestamp

          // Clear progress state when we get actual content
          updatedMessage.isProgress = false
          updatedMessage.currentStatus = null
          updatedMessage.statusType = null

          // Construct display content from parts when needed (not stored permanently)
          // Content field only used for UI display compatibility, parts array is the source of truth
          updatedMessage.content = constructContentFromParts(updatedMessage.parts)

          // Recompute essential analytics fields only
          updatedMessage.streamingDuration = computeStreamingDuration(updatedMessage.parts)
          updatedMessage.isError =
            partData.type === 'error' || updatedMessage.parts.some((p) => p.type === 'error')
        }

        newMessages[lastIndex] = updatedMessage
        return newMessages
      })

      // Update animation state based on message type and streaming status
      const newAnimationState = getAnimationStateForMessage(partData.type, isStreaming)
      setAssistantAnimationState(newAnimationState)

      // Force re-render
      setUpdateCounter((c) => c + 1)
    },
    [
      computeStreamingDuration,
      constructContentFromParts,
      getAnimationStateForMessage,
      isStreaming,
      parseComponentMarker,
    ]
  )

  /**
   * Adds a placeholder for the assistant's response.
   * Used when starting to receive streaming responses.
   * Generates UUIDs for message tracking.
   *
   * @returns {void}
   */
  const addAssistantPlaceholder = useCallback(() => {
    const assistantMessage = createMessageWithParts('assistant')
    setMessages((prev) => [...prev, assistantMessage])
  }, [createMessageWithParts])

  /**
   * Updates the last assistant message with new content.
   * Now uses the new parts-based system while maintaining backward compatibility.
   *
   * @param {string|Object} content - The message content or clickable response object
   * @param {string} messageType - Type of message ('progress', 'status', 'error', 'response', 'clickableresponse')
   * @param {boolean} isError - Whether this is an error message (for resumed conversations)
   * @param {string} trace - Error stack trace (only shown to admin users)
   */
  const updateAssistantMessage = useCallback(
    (content, messageType, isError = false, trace = null) => {
      // Convert old API to new parts API
      const partData = {
        type: messageType,
        message: typeof content === 'string' ? content : content.content || content.message || '',
        isError,
        trace,
      }

      // Handle object content (components, sentences, etc.)
      if (typeof content === 'object') {
        Object.assign(partData, {
          ...content, // Spread all object properties
          message: content.content || content.message || '', // Ensure message field exists
        })
      }

      // Special handling for clickableresponse type
      if (messageType === 'clickableresponse') {
        partData.type = 'response' // Store as response type
        partData.isComponent = true
        partData.componentType = 'clickable'
      }

      // Add the part using new system
      addMessagePart(partData)

      // Update animation state based on message type and streaming status
      const newAnimationState = getAnimationStateForMessage(messageType, isStreaming)
      setAssistantAnimationState(newAnimationState)
    },
    [addMessagePart, getAnimationStateForMessage, isStreaming]
  )

  /**
   * Removes the last user message from the chat.
   * Used when an error occurs sending a message.
   * Deactivates chat mode if no messages remain.
   *
   * @returns {void}
   */
  const removeLastUserMessage = useCallback(() => {
    setMessages((prev) => {
      const filtered = prev.slice(0, -1)
      if (filtered.length === 0) {
        setIsChatMode(false)
      }
      return filtered
    })
  }, [])

  /**
   * Cleans up the current SSE connection.
   * Safely disconnects and nullifies the SSE reference.
   *
   * @returns {void}
   */
  const cleanupSSE = useCallback(() => {
    if (sseRef.current) {
      sseRef.current.disconnect()
      sseRef.current = null
    }
  }, [])

  /**
   * Gets the conversation history excluding progress messages.
   * Used to provide context for subsequent API calls.
   *
   * @returns {Array<Object>} Array of user and completed assistant messages
   */
  const getConversationHistory = useCallback(
    () =>
      messages.filter(
        (msg) => msg.role === 'user' || (msg.role === 'assistant' && !msg.isProgress)
      ),
    [messages]
  )

  /**
   * Gets the count of user messages in the conversation.
   *
   * @returns {number} Number of user messages
   */
  const getMessageCount = useCallback(
    () => messages.filter((msg) => msg.role === 'user').length,
    [messages]
  )

  /**
   * Clears all messages and resets chat state.
   * Cleans up SSE connections and deactivates chat mode.
   * Clears conversation UUID tracking.
   *
   * @returns {void}
   */
  const clearMessages = useCallback(() => {
    setMessages([])
    setIsChatMode(false)
    setIsStreaming(false)
    cleanupSSE()

    // Clear conversation history via AuthContext
    updateConversationHistory([])

    // Clear conversation UUID tracking
    clearConversationId()
  }, [cleanupSSE, updateConversationHistory, clearConversationId])

  return {
    messages,
    isChatMode,
    isStreaming,
    updateCounter,
    assistantAnimationState,
    sseRef,
    addUserMessage,
    addAssistantPlaceholder,
    updateAssistantMessage,
    removeLastUserMessage,
    setIsStreaming,
    cleanupSSE,
    getConversationHistory,
    getMessageCount,
    clearMessages,

    // New parts-based functions
    addMessagePart,
    createMessageWithParts,

    // UUID functions
    getCurrentConversationId: getOrCreateConversationId,
    getMessageWithUUIDs: (message) => ({
      // Helper function to get complete UUID data for a message
      ...message,
      conversationId: message.conversationId || getOrCreateConversationId(),
      messageId: message.messageId,
    }),
  }
}
